// Learn tab progress store (issue #3470).
//
// The record of truth is `user.learn` on the active household member: the client caches the
// household in local storage and syncs it with the server (use_household.tsx), so the hub reads
// progress from there and switching the active member switches the hub. Exercises write many
// small updates per session, so instead of a server write per action the client keeps a
// write-behind copy per active member under LocalStorageKey.LearnProgressPending - one of the
// keys prefixed by the active user token, which is what keeps the members' copies apart - and
// pushes it to the user at flush points through server_api.upsertUser (the server merges the
// stored user under the request, so sending the whole user with `learn` set updates it).
//
// Flush points:
//  - session end and range save: the exercise slices call `flush()` explicitly
//  - the app going to the background (document hidden / pagehide): subscribed by the hook
//  - mount of the hook: a copy left behind by a crash reaches the server on the next launch
// A push that fails (offline) keeps the pending copy for the next flush point. Note that a fetch
// started from a pagehide handler is not guaranteed to complete (even with `keepalive`), which is
// why a copy can be left behind and why the mount flush exists.
//
// `updatedAt` decides which record wins: the pending copy is used and pushed only when it is at
// least as new as `user.learn`; a pending copy older than the server record (progress recorded on
// another device since) is dropped rather than overwriting the newer record.
//
// Device-specific settings that later slices add (rhythm latency calibration, the sing-along
// default) stay in local storage under their own keys and never go on the user.

import { useCallback, useEffect, useMemo } from 'react';
import { emptyLearnProgress } from '../../../common/src/learn_rules.ts';
import { LearnProgress, User } from '../../../common/src/model.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { localStorageGet, LocalStorageKey, localStorageRemove, localStorageSet } from './client_local_storage.ts';
import { useLocalStorage } from './use_local_storage.tsx';
import { getUser, useUser } from './use_user.tsx';

export type LearnProgressUpdate = (progress: LearnProgress) => LearnProgress;

export function useLearnProgress(): {
  progress: LearnProgress; // the pending copy when newer, else user.learn, else an empty record
  hasRange: boolean;
  updateProgress: (update: LearnProgressUpdate) => void; // writes the pending copy only; no server call
  flush: () => Promise<void>; // pushes the pending copy to the user, if any
} {
  const { user } = useUser();
  const [pending] = useLocalStorage(LocalStorageKey.LearnProgressPending);
  const storedProgress = user?.learn;

  const progress = useMemo(
    () => resolveLearnProgress({ stored: storedProgress, pending, now: new Date() }),
    [storedProgress, pending],
  );

  const updateProgress = useCallback((update: LearnProgressUpdate) => {
    const activeUser = getUser();
    if (!activeUser) {
      return; // nobody to record progress for
    }
    const current = resolveLearnProgress({
      stored: activeUser.learn,
      pending: localStorageGet(LocalStorageKey.LearnProgressPending),
      now: new Date(),
    });
    localStorageSet(LocalStorageKey.LearnProgressPending, update(current));
  }, []);

  // flush points: mount (a copy left behind by a previous run) and the app going to the background
  useEffect(() => {
    void flushLearnProgress();
    const controller = new AbortController();
    const flushWhenHidden = () => {
      if (document.visibilityState === 'hidden') {
        void flushLearnProgress();
      }
    };
    window.addEventListener('pagehide', () => void flushLearnProgress(), { signal: controller.signal });
    document.addEventListener('visibilitychange', flushWhenHidden, { signal: controller.signal });
    return () => controller.abort();
  }, []);

  return { progress, hasRange: progress.range !== undefined, updateProgress, flush: flushLearnProgress };
}

export function resolveLearnProgress({
  stored,
  pending,
  now,
}: {
  stored: LearnProgress | undefined;
  pending: LearnProgress | undefined;
  now: Date;
}): LearnProgress {
  if (pending && !isStale(pending, stored)) {
    return pending;
  }
  return stored ?? emptyLearnProgress(now);
}

// true when the server record is strictly newer than the local copy
function isStale(pending: LearnProgress, stored: LearnProgress | undefined): boolean {
  return stored !== undefined && Date.parse(stored.updatedAt) > Date.parse(pending.updatedAt);
}

// Pushes are serialized: a flush requested while one is in flight runs after it, so an update made
// during the request is pushed too rather than skipped. Never rejects - a failed push is logged and
// the pending copy is kept for the next flush point.
let flushInFlight: Promise<void> | undefined;

export function flushLearnProgress(): Promise<void> {
  const flush = (flushInFlight ?? Promise.resolve()).then(pushPendingLearnProgress);
  flushInFlight = flush;
  void flush.then(() => {
    if (flushInFlight === flush) {
      flushInFlight = undefined;
    }
  });
  return flush;
}

async function pushPendingLearnProgress(): Promise<void> {
  try {
    await pushPendingLearnProgressOrThrow();
  } catch (error: any) {
    console.error(`Failed to push Learn progress: ${error.message}`);
  }
}

async function pushPendingLearnProgressOrThrow(): Promise<void> {
  const user = getUser();
  const pending = localStorageGet(LocalStorageKey.LearnProgressPending);
  if (!user || !pending) {
    return;
  }
  if (isStale(pending, user.learn)) {
    localStorageRemove(LocalStorageKey.LearnProgressPending); // never overwrite a newer server record
    return;
  }
  const updatedUser: User = { ...user, learn: pending };
  await server_api.upsertUser(updatedUser);
  // Update the cached household's user directly rather than through useHousehold().setHousehold:
  // that path also marks the household for a push, and the household upsert never writes users,
  // so the push would be a wasted request. The daily household sync pulls the same user back.
  const household = localStorageGet(LocalStorageKey.Household);
  if (household) {
    const users = household.users.map((member) => (member.token === user.token ? updatedUser : member));
    localStorageSet(LocalStorageKey.Household, { ...household, users });
  }
  // an update recorded (or a member switch) during the request leaves a different pending copy: keep it
  const current = localStorageGet(LocalStorageKey.LearnProgressPending);
  if (current && JSON.stringify(current) === JSON.stringify(pending)) {
    localStorageRemove(LocalStorageKey.LearnProgressPending);
  }
}
